import { useMemo } from 'react';

import { type Query, useQueries } from '@tanstack/react-query';

import { useUtilityContext } from '@change-corgi/core/react/utilityContext';

import type {
	PetitionReadyToSharePostsByPlatformQuery,
	ReadyToSharePostWithPlatform,
} from 'src/app/shared/api/readyToShare';
import { getPetitionReadyToSharePostsByPlatform } from 'src/app/shared/api/readyToShare';

export type Platform = 'FACEBOOK' | 'INSTAGRAM' | 'REDDIT' | 'FACEBOOKGROUPS';
export type PlatformAssetStatus = 'PENDING' | 'COMPLETE' | 'FAILED' | 'NOT_AVAILABLE';

export type PlatformData = {
	totalCount: number;
	items: ReadyToSharePostWithPlatform[];
	status: PlatformAssetStatus;
	postsCount: number;
	errorMessage?: string | null;
};

export type PlatformDataMap = Record<Platform, PlatformData | undefined>;

type UseFetchReadyToSharePostsByPlatformProps = {
	slug: string;
	platforms: readonly Platform[];
	shouldExecute: boolean;
	pollingInterval?: number;
};

const POLLING_INTERVAL = 3000; // 3 seconds
const MAX_RETRIES = 5;

type CreateQueryParams = {
	platform: Platform;
	slug: string;
	utilityContext: ReturnType<typeof useUtilityContext>;
	shouldExecute: boolean;
	pollingInterval: number;
};

async function fetchPlatformData(
	slug: string,
	platform: Platform,
	utilityContext: ReturnType<typeof useUtilityContext>,
): Promise<PlatformData> {
	const result: PetitionReadyToSharePostsByPlatformQuery | null = await getPetitionReadyToSharePostsByPlatform(
		{ slugOrId: slug, platform },
		utilityContext,
	);

	// Type guard: Check if result exists and petition was found
	if (!result?.petitionBySlugOrId) {
		throw new Error('Failed to fetch ready to share posts');
	}

	const { readyToSharePosts } = result.petitionBySlugOrId;

	// Type guard: Handle failure union type
	if (readyToSharePosts.__typename === 'ReadyToSharePostsFailure') {
		return {
			totalCount: 0,
			items: [],
			status: 'FAILED',
			postsCount: 0,
			errorMessage: readyToSharePosts.message,
		};
	}

	// Type guard: Success case (TypeScript now knows this is ReadyToSharePostsSuccess)
	const items = [...readyToSharePosts.items] as ReadyToSharePostWithPlatform[];

	return {
		totalCount: items.length,
		items,
		status: items.length > 0 ? 'COMPLETE' : 'NOT_AVAILABLE',
		postsCount: items.length,
		errorMessage: null,
	};
}

function createPlatformQuery({ platform, slug, utilityContext, shouldExecute, pollingInterval }: CreateQueryParams) {
	return {
		queryKey: ['readyToSharePosts', slug, platform] as const,
		queryFn: async () => fetchPlatformData(slug, platform, utilityContext),
		enabled: shouldExecute,
		retry: MAX_RETRIES,
		retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchInterval: (query: Query<PlatformData, Error>) => {
			const { data } = query.state;
			// Stop polling when we have a terminal state (data available, not available, or failed)
			// Continue polling if data is undefined (still loading/retrying)
			if (data && ['COMPLETE', 'NOT_AVAILABLE', 'FAILED'].includes(data.status)) {
				return false;
			}
			return pollingInterval;
		},
		staleTime: 0,
	};
}

function calculatePlatformDataFromQueries(
	queriesData: Array<PlatformData | undefined>,
	queriesState: Array<{ isLoading: boolean; isFetching: boolean }>,
	platforms: readonly Platform[],
): Record<Platform, PlatformData | undefined> {
	const data: Record<Platform, PlatformData | undefined> = {} as Record<Platform, PlatformData | undefined>;
	queriesData.forEach((queryData, index) => {
		const platform = platforms[index];
		const state = queriesState[index];

		// Handle PENDING state when query is loading or fetching
		if (state.isLoading || state.isFetching) {
			data[platform] = {
				status: 'PENDING',
				items: [],
				totalCount: 0,
				postsCount: 0,
				errorMessage: null,
			};
		} else if (queryData) {
			data[platform] = queryData;
		}
	});
	return data;
}

export function useFetchReadyToSharePostsByPlatform({
	slug,
	platforms,
	shouldExecute,
	pollingInterval = POLLING_INTERVAL,
}: UseFetchReadyToSharePostsByPlatformProps) {
	const utilityContext = useUtilityContext();

	const queries = useQueries({
		queries: platforms.map((platform) =>
			createPlatformQuery({ platform, slug, utilityContext, shouldExecute, pollingInterval }),
		),
	});

	// Extract data from queries to avoid unstable dependencies
	const queriesData = queries.map((q) => q.data);
	const queriesState = queries.map((q) => ({ isLoading: q.isLoading, isFetching: q.isFetching }));

	const platformData = useMemo(
		() => calculatePlatformDataFromQueries(queriesData, queriesState, platforms),
		[queriesData, queriesState, platforms],
	);

	const { allComplete, anyFailed, anyPending, hasAnyData, allDone } = useMemo(() => {
		const statuses = Object.values(platformData).filter((d): d is PlatformData => d !== undefined);
		return {
			allComplete: statuses.length > 0 && statuses.every((d) => d.status === 'COMPLETE'),
			anyFailed: statuses.some((d) => d.status === 'FAILED'),
			anyPending: statuses.some((d) => d.status === 'PENDING'),
			hasAnyData: statuses.some((d) => d.status === 'COMPLETE' && d.postsCount > 0),
			allDone:
				statuses.length === platforms.length &&
				statuses.every((d) => ['COMPLETE', 'NOT_AVAILABLE', 'FAILED'].includes(d.status)),
		};
	}, [platformData, platforms]);

	// Show loading only if ALL queries are still loading AND none have data yet
	// Once any query returns data, hide the loading state
	const isLoading = queries.every((q) => q.isLoading) && !hasAnyData;
	const allFailed = queries.length > 0 && queries.every((q) => q.isError);

	return {
		platformData,
		isLoading,
		allComplete,
		anyFailed,
		anyPending,
		hasAnyData,
		allDone,
		allFailed,
		queries,
	};
}

export type UseFetchReadyToSharePostsByPlatformReturn = ReturnType<typeof useFetchReadyToSharePostsByPlatform>;
